// ---------------------------------------------------------------------------
// Calendar-confidentiality redactor + decision helper (ENG-5840).
//
// Pure, framework-agnostic core of the `calendar.confidentiality` guardrail.
// EA-style agents read their principal's calendar via Google Calendar tools;
// when a non-principal asks the agent "is Brad free at 2pm?" the agent leaks
// meeting title, attendees, location — confidential information about the
// principal's relationships and work.
//
// This module redacts those fields from Google Calendar tool responses,
// preserving timing + busy/free state so the agent stays useful for
// scheduling. The runtime enforcement layer (packages/api/src/lib/
// calendar-confidentiality-guard.ts) decides WHEN to apply the redaction;
// this module is the WHAT.
//
// Contract:
//   • Mode 'redact'       → strip summary / description / attendees /
//                           location / hangoutLink / conferenceData /
//                           organizer.email; preserve start / end /
//                           status / transparency / id / iCalUID / eventType
//                           / all-day flags.
//   • Mode 'pass-through' → return unchanged.
//
// The redactor is shape-tolerant — Composio + Google Calendar responses
// vary by tool, so anything we don't explicitly preserve gets dropped.
// Better to over-redact a new field than leak it (deny by default for
// any field we haven't classified).
//
// Decision (decideCalendarRedactionMode) is pure with respect to its
// inputs: caller identity vs principal identity from agent.reports_to.
// Returns 'pass-through' iff we can prove the caller IS the principal;
// returns 'redact' on any uncertainty (caller unknown, principal unknown,
// no match). Fail-closed.
// ---------------------------------------------------------------------------

export const CALENDAR_CONFIDENTIALITY_DEFINITION_ID = 'calendar.confidentiality';

/**
 * The rollout stage governing whether the calendar guard ACTS or only observes.
 *
 * ENG-10104: lifted here so the CLAUDE.md renderer and the console read the
 * stage the same way. `redactCalendarToolResponseIfNeeded` returns the response
 * unchanged at anything but `enforce`, so a surface that shows `enforce` for a
 * shadow-stage row is claiming a redaction that does not happen.
 *
 * `packages/api/src/lib/calendar-confidentiality-guard.ts` has its own
 * `parseStage` with identical semantics. It is deliberately NOT converged here
 * in this change — that guard is the enforcement point and rewiring it is a
 * behaviour change to a live control, which does not belong in a rendering fix.
 * Any divergence would show up as the console and the runtime disagreeing, which
 * is the class this ticket is about, so it is worth converging next time either
 * is touched.
 */
export type CalendarGuardStage = 'shadow' | 'warn' | 'enforce';

export function parseCalendarGuardStage(raw: unknown): CalendarGuardStage {
  if (raw === 'warn' || raw === 'enforce') return raw;
  return 'shadow';
}

/**
 * The four Google Calendar tools whose responses leak meeting content.
 * Matched as case-insensitive prefix-or-exact on the Composio tool name
 * (Composio normalises to `GOOGLECALENDAR_<ACTION>` upper-snake) AND
 * accepts the lower-snake variant used in some MCP wrappers.
 *
 * Keep this list narrow — adding a calendar tool here without confirming
 * its response shape means the redactor might silently pass leaked
 * content through.
 */
export const CALENDAR_READ_TOOL_PATTERNS: readonly string[] = [
  'googlecalendar_find_events',
  'googlecalendar_get_event',
  'googlecalendar_list_calendars',
  'googlecalendar_find_free_slots',
  // Composio also ships these older aliases on some toolkits — match both
  // so we cover the call shape an agent might actually emit.
  'googlecalendar_events_list',
  'googlecalendar_events_get',
  'googlecalendar_calendars_list',
  'googlecalendar_freebusy_query',
];

export function isCalendarReadTool(toolName: string | undefined): boolean {
  if (!toolName) return false;
  const normalised = toolName.toLowerCase();
  return CALENDAR_READ_TOOL_PATTERNS.includes(normalised);
}

/** Fields we KEEP after redaction. Anything else on an event is dropped. */
export const CALENDAR_EVENT_PRESERVED_FIELDS: readonly string[] = [
  'id',
  'iCalUID',
  'start',
  'end',
  'status',
  'transparency',
  'eventType',
  'recurringEventId',
  'recurrence',
  // All-day flags. Google represents all-day events with `start.date` /
  // `end.date` (instead of `start.dateTime`) — those are already inside
  // the preserved `start`/`end`. Explicit listing here for clarity.
  // Operators reading the runbook should see exactly what stays.
];

/** Fields we EXPLICITLY drop. Documenting the leak surface. */
export const CALENDAR_EVENT_REDACTED_FIELDS: readonly string[] = [
  'summary',
  'description',
  'attendees',
  'location',
  'hangoutLink',
  'conferenceData',
  'organizer',
  'creator',
  'attachments',
];

export type CalendarRedactionMode = 'pass-through' | 'redact';

/**
 * Caller + principal identity used to decide the redaction mode.
 *
 * Identity is matched on either platform id (Slack user_id, Teams AAD
 * object id) — both are workspace-stable and resistant to display-name
 * spoofing. Email is intentionally NOT a match key: it's easier to
 * impersonate (forwarded mail, alias) and the agent rarely has the
 * caller's verified email in any case.
 */
export interface CalendarRedactionDecisionInput {
  /** Slack user_id of the caller (sender of the inbound message). */
  callerSlackUserId?: string | null;
  /** Teams AAD object id of the caller. */
  callerTeamsAadObjectId?: string | null;
  /** Telegram chat_id of the caller. */
  callerTelegramChatId?: string | null;
  /** Slack user_id resolved from agent.reports_to_person → contact_preferences. */
  principalSlackUserId?: string | null;
  /** Teams AAD object id resolved from agent.reports_to_person. */
  principalTeamsAadObjectId?: string | null;
  /** Telegram chat_id resolved from agent.reports_to_person. */
  principalTelegramChatId?: string | null;
}

export interface CalendarRedactionDecision {
  mode: CalendarRedactionMode;
  /**
   * Set when mode = 'pass-through' — which platform id matched. Captures
   * the audit signal "we matched on Slack vs Teams vs Telegram" so a
   * reviewer reading guardrail_audit_log can verify the matcher worked.
   */
  matchedPlatform?: 'slack' | 'teams' | 'telegram';
  /**
   * Set when mode = 'redact' — the reason. Useful for telemetry +
   * SOC2 audit: distinguishes "no principal configured" (operator
   * action needed) from "caller is just not the principal" (working
   * as designed).
   */
  redactionReason?:
    | 'no_principal_configured'
    | 'no_caller_identified'
    | 'caller_not_principal';
}

/**
 * Pure decision: pass-through iff we can prove caller IS principal.
 * Anything else (no principal, no caller, no match) returns 'redact'.
 * Fail-closed contract — see module header.
 */
export function decideCalendarRedactionMode(
  input: CalendarRedactionDecisionInput,
): CalendarRedactionDecision {
  const hasAnyPrincipalId =
    !!input.principalSlackUserId ||
    !!input.principalTeamsAadObjectId ||
    !!input.principalTelegramChatId;
  if (!hasAnyPrincipalId) {
    return { mode: 'redact', redactionReason: 'no_principal_configured' };
  }

  const hasAnyCallerId =
    !!input.callerSlackUserId ||
    !!input.callerTeamsAadObjectId ||
    !!input.callerTelegramChatId;
  if (!hasAnyCallerId) {
    return { mode: 'redact', redactionReason: 'no_caller_identified' };
  }

  // Match on any platform id that BOTH sides have. The first match wins
  // — there's no scenario where the same person would have a mismatched
  // pair on two platforms (a Slack id matching the principal AND a Teams
  // id matching someone else would mean the resolver lied, in which case
  // we already have a more serious problem).
  if (
    input.callerSlackUserId &&
    input.principalSlackUserId &&
    input.callerSlackUserId === input.principalSlackUserId
  ) {
    return { mode: 'pass-through', matchedPlatform: 'slack' };
  }
  if (
    input.callerTeamsAadObjectId &&
    input.principalTeamsAadObjectId &&
    input.callerTeamsAadObjectId === input.principalTeamsAadObjectId
  ) {
    return { mode: 'pass-through', matchedPlatform: 'teams' };
  }
  if (
    input.callerTelegramChatId &&
    input.principalTelegramChatId &&
    input.callerTelegramChatId === input.principalTelegramChatId
  ) {
    return { mode: 'pass-through', matchedPlatform: 'telegram' };
  }

  return { mode: 'redact', redactionReason: 'caller_not_principal' };
}

// ── Redactor ────────────────────────────────────────────────────────────────

/**
 * Redact a single calendar event. Returns a NEW object containing only
 * the preserved fields. Anything else (including unknown fields we
 * haven't classified) is dropped — defensive default.
 *
 * If `event` isn't an object, returns it unchanged: malformed input is
 * the caller's problem to handle, not ours to over-interpret.
 */
export function redactCalendarEvent(event: unknown): unknown {
  if (event === null || typeof event !== 'object') return event;
  const source = event as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const field of CALENDAR_EVENT_PRESERVED_FIELDS) {
    if (field in source) out[field] = source[field];
  }
  // Mark the redaction so the agent's prompt context shows a clear
  // signal "this event was redacted by the calendar-confidentiality
  // guardrail" — the agent can then phrase its reply as "another
  // meeting" / "busy" without inventing details that aren't there.
  out['_augmented_redacted'] = true;
  out['_augmented_redaction_reason'] = 'calendar-confidentiality';
  return out;
}

/**
 * Redact a typical Google Calendar list-events response payload.
 * Tolerant of the two shapes Composio returns: `{ items: [...] }`
 * (native Google API) and a flat array (some Composio toolkits
 * normalise the wrapper away). Other shapes pass through with a
 * defensive deep-walk to catch nested `items` / `events` arrays.
 */
export function redactCalendarListResponse(response: unknown): unknown {
  if (response === null || typeof response !== 'object') return response;

  // Flat array of events.
  if (Array.isArray(response)) {
    return response.map((evt) => redactCalendarEvent(evt));
  }

  const obj = response as Record<string, unknown>;
  const out: Record<string, unknown> = { ...obj };

  // Common Google shape: `items` or `events` carries the event list.
  for (const arrayKey of ['items', 'events']) {
    const value = obj[arrayKey];
    if (Array.isArray(value)) {
      out[arrayKey] = value.map((evt) => redactCalendarEvent(evt));
    }
  }

  // Get-event-by-id returns a bare event shape (no wrapper). If the
  // response itself looks like a single event (has a `start` field),
  // redact it whole.
  if ('start' in obj && !Array.isArray(obj['start'])) {
    return redactCalendarEvent(obj);
  }

  // freebusy responses don't carry event details — only busy/free
  // windows. Pass through unchanged; the data class isn't sensitive.
  // Listed explicitly so reviewers can see we considered it.
  if ('calendars' in obj && typeof obj['calendars'] === 'object') {
    return obj;
  }

  return out;
}

/**
 * Top-level dispatch — the runtime guard calls this with the tool name
 * and raw response. It picks the right redactor shape for the tool.
 * Returns the redacted shape (or the unchanged input for tools we
 * don't classify as needing redaction).
 */
export function redactCalendarToolResponse(
  toolName: string,
  response: unknown,
): unknown {
  if (!isCalendarReadTool(toolName)) return response;
  return redactCalendarListResponse(response);
}
